/**
 * <s-product-image-zoom> — PDP image zoom preloader.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component only warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);

    // Arrows, keyboard arrows and swipes inside an open photo step through that colour's views.
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-zoom-step]');
      if (!button || !this.contains(button)) return;
      const dialog = button.closest('dialog[data-zoom-dialog]');
      if (dialog) this.#step(dialog, Number(button.dataset.zoomStep) || 1);
    }, opts);

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const dialog = event.target instanceof Element ? event.target.closest('dialog[data-zoom-dialog]') : null;
      if (!dialog?.open) return;
      const rtl = getComputedStyle(dialog).direction === 'rtl';
      const forward = (event.key === 'ArrowRight') !== rtl;
      event.preventDefault();
      this.#step(dialog, forward ? 1 : -1);
    }, opts);

    let startX = null;
    let startY = 0;
    this.addEventListener('touchstart', (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('dialog[data-zoom-dialog]')) return;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    }, { ...opts, passive: true });
    this.addEventListener('touchend', (event) => {
      if (startX === null) return;
      const dialog = event.target instanceof Element ? event.target.closest('dialog[data-zoom-dialog]') : null;
      const dx = event.changedTouches[0].clientX - startX;
      const dy = event.changedTouches[0].clientY - startY;
      startX = null;
      if (!dialog || Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      const rtl = getComputedStyle(dialog).direction === 'rtl';
      this.#step(dialog, (dx < 0) !== rtl ? 1 : -1);
    }, { ...opts, passive: true });

    // Hide the arrows when a colour has only one view.
    this.addEventListener('toggle', (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.open || !dialog.matches('[data-zoom-dialog]')) return;
      const single = this.#group(dialog).length < 2;
      for (const arrow of dialog.querySelectorAll('[data-zoom-step]')) arrow.hidden = single;
    }, { ...opts, capture: true });
  }

  /** The views that belong with this photo: same colour first, then photos shared by every colour. */
  #group(dialog) {
    const dialogs = [...this.querySelectorAll('dialog[data-zoom-dialog]')];
    const galleryColour = this.querySelector('s-product-gallery')?.dataset.colour || '';
    const colour = dialog.dataset.colour || galleryColour;
    if (!colour) return dialogs;
    const own = dialogs.filter((d) => d.dataset.colour === colour);
    const shared = dialogs.filter((d) => !d.dataset.colour);
    return [...own, ...shared];
  }

  #step(dialog, direction) {
    const group = this.#group(dialog);
    if (group.length < 2) return;
    const index = Math.max(0, group.indexOf(dialog));
    const next = group[(index + direction + group.length) % group.length];
    const image = next.querySelector('img[data-zoom-image]');
    if (image instanceof HTMLImageElement) image.loading = 'eager';
    dialog.close();
    next.showModal();
    next.querySelector(`[data-zoom-step="${direction > 0 ? 1 : -1}"]`)?.focus({ preventScroll: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
